import { useCallback, useEffect, useRef, useState } from 'react';
import { useBlocker, useNavigate } from 'react-router';
import { Dialog, Notice } from '../../components/ui.js';
import { useOffline } from '../../offline/context.js';
import { openStore } from '../../offline/local.js';
import type { LocalStore } from '../../offline/store.js';
import { useSignedIn } from '../../session.js';
import { SUBJECTS, capture, fromFile, guideStyle, openCamera, type SubjectId } from './camera.js';
import {
  ScanPerson,
  dropDraft,
  keepDraft,
  loadDraft,
  newDraft,
  type ScanDraft,
} from './ScanPerson.js';

type Stage = 'camera' | 'preview' | 'person';

/**
 * Scan (brief section 6.3, decisions of Nov 3, 2026), as docs/prototype.html:
 * the camera and its red guide (WR Scanner), or a chosen photo; Retake or
 * Confirm. The camera stays open while Scan shows (asked once). Confirm
 * saves nothing: the photo waits on this device, encrypted, while the card
 * is read and the seller checks it (ScanPerson); the contact exists, and
 * goes to HubSpot, only at Save. A scan being edited when the app closed
 * comes back. The cross, Back or a menu ask Discard this scan?
 */
export function Scan() {
  const { user } = useSignedIn();
  const { store, counts } = useOffline();
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>('camera');
  const [subject, setSubject] = useState<SubjectId>('card');
  const [photo, setPhoto] = useState<{ blob: Blob; url: string } | null>(null);
  const [draft, setDraft] = useState<ScanDraft | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [today, setToday] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);
  const leaving = useRef(false);
  const { videoRef, guideRef, ready, shoot: shootCamera, resume } = useCamera(true);
  const current = SUBJECTS.find((x) => x.id === subject)!;
  /** This person's store on this device (opened if the app has not yet). */
  const device = () => (store ? Promise.resolve(store) : openStore(user.id));
  // The draft's writes, one after the other: the last edit is always the one kept.
  const writes = useRef<Promise<unknown>>(Promise.resolve());
  // The store is the one of now: a write never lands in another person's store.
  const keep = (d: ScanDraft, withPhoto = false) => {
    const target = device();
    writes.current = writes.current
      .then(() => target)
      .then((opened) => keepDraft(opened, d, withPhoto))
      .catch(() => undefined);
  };
  const drop = () => {
    const target = device();
    writes.current = writes.current
      .then(() => target)
      .then(dropDraft)
      .catch(() => undefined);
  };

  useEffect(() => {
    if (!store) return;
    let current = true;
    void scansToday(store, user.id).then((n) => current && setToday(n));
    return () => {
      current = false;
    };
  }, [store, counts, user.id]);

  // A scan being edited when the app closed comes back: nothing is lost before Save.
  useEffect(() => {
    let current = true;
    void openStore(user.id)
      .then(loadDraft)
      .then((kept) => {
        if (!current || !kept) return;
        setPhoto({ blob: kept.photo, url: URL.createObjectURL(kept.photo) });
        setDraft(kept);
        setStage('person');
      })
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, [user.id]);

  // The preview's address is freed with the photo.
  useEffect(() => () => (photo ? URL.revokeObjectURL(photo.url) : undefined), [photo]);

  // A photo not saved yet: ask before leaving (Back, a menu, a link).
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !leaving.current && stage !== 'camera' && currentLocation.pathname !== nextLocation.pathname,
  );

  function took(blob: Blob) {
    setPhoto({ blob, url: URL.createObjectURL(blob) });
    setProblem(null);
    setStage('preview');
  }

  async function shoot() {
    const blob = await shootCamera();
    if (blob) took(blob);
  }

  async function chosen(file: File | undefined) {
    if (!file) return;
    try {
      took(await fromFile(file));
    } catch {
      setProblem('This file is not a photo the portal can read. Choose another one.');
    }
  }

  /** Back to the viewfinder, the camera still open. */
  function restart() {
    setPhoto(null);
    setDraft(null);
    setStage('camera');
    resume();
  }

  /** Confirm: the photo waits on this device while the card is read; nothing is saved yet. */
  function confirm() {
    if (!photo) return;
    const next = newDraft(photo.blob);
    setDraft(next);
    setStage('person');
    keep(next, true);
  }

  /** Leaves Scan for `to`, the scan given up. */
  function leave(to: string) {
    drop();
    leaving.current = true;
    if (blocker.state === 'blocked') blocker.proceed?.();
    else navigate(to, { replace: true });
  }

  function discard() {
    setAsking(false);
    leave('/');
  }

  const closing = asking || blocker.state === 'blocked';

  return (
    <div className="scan-wrap">
      <div className="scan-head">
        <h1 className="page">Scan</h1>
        {stage !== 'camera' && (
          <button type="button" className="x" aria-label="Close" onClick={() => setAsking(true)}>
            ×
          </button>
        )}
      </div>

      <div hidden={stage !== 'camera'}>
        <div className="vf">
          <video ref={videoRef} playsInline muted aria-hidden="true" />
          <div ref={guideRef} className="vf-guide" style={guideStyle(current)}>
            <span />
            <span />
            <span />
            <span />
          </div>
          <p className="vf-tip">
            {ready === false
              ? 'No camera here. Allow the camera in the browser, or choose a photo.'
              : `Fit the ${current.label.toLowerCase()} inside the red frame. Only what is inside is sent.`}
          </p>
        </div>
        <div className="vf-bar">
          <div className="seg" role="group" aria-label="What are you scanning">
            {SUBJECTS.map((s) => (
              <button
                key={s.id}
                type="button"
                aria-pressed={s.id === subject}
                onClick={() => setSubject(s.id)}
              >
                {s.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="shutter"
            aria-label="Take the photo"
            disabled={!ready || stage !== 'camera'}
            onClick={() => void shoot()}
          />
          <span className="note">
            {today} scan{today === 1 ? '' : 's'} today
          </span>
        </div>
        <div className="actions" style={{ justifyContent: 'center', marginTop: 10 }}>
          <button type="button" className="btn sm" onClick={() => fileRef.current?.click()}>
            Choose a photo
          </button>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          aria-label="Photo of the card"
          onChange={(e) => {
            void chosen(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
        {problem && <Notice kind="error">{problem}</Notice>}
      </div>

      {stage === 'preview' && photo && (
        <>
          <div className="vf vf-prev">
            <img src={photo.url} alt="Captured image" />
          </div>
          <div className="g2" style={{ marginTop: 12 }}>
            <button type="button" className="btn block" onClick={restart}>
              Retake
            </button>
            <button type="button" className="btn primary block" onClick={confirm}>
              Confirm
            </button>
          </div>
        </>
      )}

      {stage === 'person' && photo && draft && (
        <ScanPerson
          key={draft.id}
          draft={draft}
          photoUrl={photo.url}
          onEdits={(edits) => {
            keep({ ...draft, edits });
          }}
          onSaved={(next) => {
            drop();
            if (next === 'scan') return restart();
            leaving.current = true;
            navigate('/', { replace: true });
          }}
          onClose={() => setAsking(true)}
          onDiscard={(to) => leave(to)}
        />
      )}

      {closing && (
        <Dialog
          small
          title="Discard this scan?"
          onClose={() => {
            setAsking(false);
            blocker.reset?.();
          }}
          footer={
            <>
              <button
                type="button"
                className="btn"
                autoFocus
                onClick={() => {
                  setAsking(false);
                  blocker.reset?.();
                }}
              >
                Keep editing
              </button>
              <button type="button" className="btn primary" onClick={discard}>
                Discard
              </button>
            </>
          }
        >
          <p className="note">Nothing of this scan is saved yet.</p>
        </Dialog>
      )}
    </div>
  );
}

/**
 * The live camera (WR Scanner): opened once when Scan opens and kept open
 * while Scan shows, the viewfinder only hidden during the preview and the
 * form (Nov 3, 2026: the browser asks for the camera once, not at every
 * card); opened again when the phone turns (the track keeps the orientation
 * it was opened in) or when the system closed it. ready is false without a
 * camera (refused, none on this computer): Choose a photo still works.
 */
function useCamera(active: boolean) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState<boolean | null>(null);

  // Each opening has its number: a camera that opens after the screen moved
  // on (closed, photo taken, opened again) is closed at once.
  const opening = useRef(0);

  const stop = useCallback(() => {
    opening.current += 1;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const start = useCallback(() => {
    stop();
    const mine = opening.current;
    return openCamera().then(
      async (stream) => {
        if (mine !== opening.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }
        setReady(true);
      },
      () => setReady(false),
    );
  }, [stop]);

  useEffect(() => {
    if (!active) return;
    void start();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onRotate = () => {
      clearTimeout(timer);
      timer = setTimeout(() => void start(), 350);
    };
    // Back from another app: the system may have closed the camera; only then is it opened again.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const live = streamRef.current?.getVideoTracks().some((t) => t.readyState === 'live');
      if (!live) void start();
      else void videoRef.current?.play().catch(() => undefined);
    };
    screen.orientation?.addEventListener?.('change', onRotate);
    window.addEventListener('orientationchange', onRotate);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      screen.orientation?.removeEventListener?.('change', onRotate);
      window.removeEventListener('orientationchange', onRotate);
      document.removeEventListener('visibilitychange', onVisible);
      stop();
    };
  }, [active, start, stop]);

  /** Back to the viewfinder: the same camera, still open (asked once per visit of Scan). */
  const resume = useCallback(() => {
    const live = streamRef.current?.getVideoTracks().some((t) => t.readyState === 'live');
    if (!live) return void start();
    void videoRef.current?.play().catch(() => undefined);
  }, [start]);

  /** What is inside the guide, or null when nothing could be taken. */
  const shoot = useCallback(async () => {
    if (!videoRef.current || !guideRef.current) return null;
    return capture(videoRef.current, guideRef.current).catch(() => null);
  }, []);

  return { videoRef, guideRef, ready, shoot, resume };
}

/** The person's scans of today: those back from the server, and those still on this device. */
async function scansToday(store: LocalStore, userId: string): Promise<number> {
  const day = new Date().toDateString();
  const contacts = await store.listRecords('contact');
  return contacts.filter((c) => {
    if (c.source !== 'scan') return false;
    if (typeof c.created_at !== 'string') return true;
    return c.created_by === userId && new Date(c.created_at).toDateString() === day;
  }).length;
}
